import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot,
} from '@mui/lab';
import { Typography } from '@mui/material';
import { formatDateTime } from 'utils/emailHistory/dateUtils';
import {
  Autorenew as AutorenewIcon,
  Queue as QueueIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  OpenInBrowser as OpenInBrowserIcon,
  Link as LinkIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

/**
 * Timeline point configuration
 */
const TIMELINE_POINTS = {
  Processing: {
    label: 'Processing',
    icon: <AutorenewIcon fontSize="small" />,
    color: 'primary',
  },
  Queued: {
    label: 'Queued',
    icon: <QueueIcon fontSize="small" />,
    color: 'primary',
  },
  Sent: {
    label: 'Sent',
    icon: <SendIcon fontSize="small" />,
    color: 'primary',
  },
  Delivered: {
    label: 'Delivered',
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'success',
  },
  Opened: {
    label: 'Opened',
    icon: <OpenInBrowserIcon fontSize="small" />,
    color: 'secondary',
  },
  Clicked: {
    label: 'Clicked',
    icon: <LinkIcon fontSize="small" />,
    color: 'secondary',
  },
  Bounced: {
    label: 'Bounced',
    icon: <WarningIcon fontSize="small" />,
    color: 'error',
  },
  Failed: {
    label: 'Failed',
    icon: <ErrorIcon fontSize="small" />,
    color: 'error',
  },
};

/**
 * Normal progression order
 */
const NORMAL_PROGRESSION = ['Processing', 'Queued', 'Sent', 'Delivered', 'Opened', 'Clicked'];

/**
 * Get timeline points based on status
 */
const getTimelinePoints = (status) => {
  // Handle error states - show Processing, Queued, Sent, then the error
  if (status === 'Bounced' || status === 'Failed') {
    return ['Processing', 'Queued', 'Sent', status];
  }
  
  // For normal progression, always show all 6 points
  return NORMAL_PROGRESSION;
};

/**
 * Check if a point should be colored or greyed out
 */
const isPointActive = (point, currentStatus) => {
  // For error states, all points in the error timeline are active
  if (currentStatus === 'Bounced' || currentStatus === 'Failed') {
    // Error timeline is: Processing, Queued, Sent, Bounced/Failed
    const errorTimeline = ['Processing', 'Queued', 'Sent', currentStatus];
    return errorTimeline.includes(point);
  }
  
  // For normal progression, check if point is at or before current status
  const pointIndex = NORMAL_PROGRESSION.indexOf(point);
  const statusIndex = NORMAL_PROGRESSION.indexOf(currentStatus);
  
  // If point or status not found in normal progression, return false
  if (pointIndex === -1 || statusIndex === -1) {
    return false;
  }
  
  // Point is active if it's at or before current status
  return pointIndex <= statusIndex;
};

/**
 * Get color for a timeline point
 */
const getPointColor = (point, currentStatus) => {
  const isActive = isPointActive(point, currentStatus);
  
  if (!isActive) {
    return 'grey';
  }
  
  // Error states get error color
  if (point === 'Bounced' || point === 'Failed') {
    return 'error';
  }
  
  // Return the configured color for the point
  return TIMELINE_POINTS[point]?.color || 'primary';
};

/**
 * Map event types from API to timeline point names
 */
const mapEventTypeToPoint = (eventType) => {
  const mapping = {
    'Delivered': 'Delivered',
    'Opened': 'Opened',
    'Link Clicked': 'Clicked',
    'Delivery Failed': 'Failed',
    'Bounced': 'Bounced',
  };
  return mapping[eventType] || null;
};

/**
 * Get timestamp for a timeline point from events array
 */
const getTimestampForPoint = (point, events = []) => {
  // Find the event that matches this point
  const event = events.find(e => {
    const mappedPoint = mapEventTypeToPoint(e.eventType);
    return mappedPoint === point;
  });
  
  return event?.timestamp || null;
};

/**
 * @component EventTimeline
 * @description Vertical timeline showing email status progression
 * @param {Object} props
 * @param {string} props.status - Current email status (Processing, Queued, Sent, Delivered, Opened, Clicked, Bounced, Failed)
 * @param {Array<{eventType: string, timestamp: string, details?: string|null}>} [props.events=[]] - Array of email events with timestamps
 * @returns {JSX.Element}
 */
export function EventTimeline({ status, events = [] }) {
  if (!status) {
    return (
      <Typography variant="body2" color="text.secondary">
        No status available
      </Typography>
    );
  }

  const timelinePoints = getTimelinePoints(status);

  return (
    <Timeline position="left" sx={{ mt: 0 }}>
      {timelinePoints.map((point, index) => {
        const isActive = isPointActive(point, status);
        const pointColor = getPointColor(point, status);
        const pointConfig = TIMELINE_POINTS[point];
        const isLast = index === timelinePoints.length - 1;

        // Connector before this point: colored if both previous and current are active
        const prevPoint = index > 0 ? timelinePoints[index - 1] : null;
        const prevPointActive = prevPoint ? isPointActive(prevPoint, status) : false;
        const connectorBeforeColor = (prevPointActive && isActive) ? pointColor : 'grey';
        
        // Connector after this point: colored if both current and next are active
        const nextPoint = !isLast ? timelinePoints[index + 1] : null;
        const nextPointActive = nextPoint ? isPointActive(nextPoint, status) : false;
        const connectorAfterColor = (isActive && nextPointActive) ? pointColor : 'grey';

        // Get timestamp for this point from events
        const timestamp = getTimestampForPoint(point, events);
        const formattedTimestamp = timestamp ? formatDateTime(timestamp) : null;

        return (
          <TimelineItem key={point}>
            {formattedTimestamp && (
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formattedTimestamp}
              </TimelineOppositeContent>
            )}
            <TimelineSeparator>
              {index > 0 && (
                <TimelineConnector
                  sx={{
                    bgcolor: connectorBeforeColor === 'grey' ? 'grey.300' : `${connectorBeforeColor}.main`,
                  }}
                />
              )}
              <TimelineDot
                color={pointColor === 'grey' ? undefined : pointColor}
                variant={pointColor === 'grey' ? 'outlined' : 'filled'}
                sx={{
                  ...(pointColor === 'grey' && {
                    borderColor: 'grey.300',
                    color: 'grey.400',
                  }),
                }}
              >
                {pointConfig?.icon}
              </TimelineDot>
              {!isLast && (
                <TimelineConnector
                  sx={{
                    bgcolor: connectorAfterColor === 'grey' ? 'grey.300' : `${connectorAfterColor}.main`,
                  }}
                />
              )}
            </TimelineSeparator>
            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  color: isActive ? 'text.primary' : 'text.secondary',
                }}
              >
                {pointConfig?.label || point}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
